import { useCallback, useContext, useEffect, useState } from "react";
import * as Cesium from "cesium";
import Symbol from "milsymbol";
import { createDescription } from "../../../../../common/createDescription";
import { BehaviorEditiorContext } from "../contexts/BehaviorEditiorContext";
import { getRandomPositionFromCartesians } from "../functions/getRandomPositionFromCartesians";

interface ISystemsLayer {
	viewer: Cesium.Viewer;
	systemIds: { agent_id: number; system_id: number; is_commander: boolean }[];
	scenarioSettings: Record<string, any>;
	currentArea: Cesium.Entity | null;
	behaviorSettingsProperty: string;
	onSystemsCreated: () => void;
	side: 0 | 1;
	visible: boolean;
}

export const SystemsLayer: React.FC<ISystemsLayer> = ({
	viewer,
	side,
	systemIds,
	scenarioSettings,
	currentArea,
	behaviorSettingsProperty,
	onSystemsCreated,
	visible,
}) => {
	const { allEntities, resetAgentIdEntities } = useContext(BehaviorEditiorContext);
	const [areSystemsCreated, setAreSystemsCreated] = useState(false);

	const replaceAt = (input: string, index: number, replacement: string) => {
		return input.substring(0, index) + replacement + input.substring(index + replacement.length);
	};

	const createSymbol = useCallback(
		(system: { system_id: number; agent_id: number; is_commander: boolean }) => {
			if (viewer) {
				const exists = viewer.entities.getById("symbol" + system.agent_id);

				if (!exists && currentArea?.polygon?.hierarchy) {
					const systemSettings = scenarioSettings.systems[system.system_id];
					const positions: Cesium.Cartesian3[] = currentArea?.polygon?.hierarchy.getValue(new Cesium.JulianDate(1, 1)).positions;
					const areaCenter = getRandomPositionFromCartesians(positions);

					viewer.entities.add({
						id: "symbol" + system.agent_id,
						position: Cesium.Cartesian3.fromRadians(areaCenter.longitude, areaCenter.latitude, areaCenter.height),
						properties: {
							is_commander: system.is_commander,
						},
						billboard: {
							image: new Symbol.Symbol(
								replaceAt(systemSettings.symbol, 3, behaviorSettingsProperty === "blue_behavior_settings" ? "3" : "6"),
								{
									size: 24,
								}
							).asCanvas(),
							disableDepthTestDistance: Number.POSITIVE_INFINITY,
						},
						description: createDescription({ agent_id: system.agent_id, name: systemSettings.name }),
					});
				}
			}
		},
		[viewer, currentArea, scenarioSettings, behaviorSettingsProperty]
	);

	const drawSystems = useCallback(() => {
		if (viewer && currentArea) {
			systemIds.forEach((system) => {
				const exists = viewer.entities.getById("symbol" + system.agent_id);
				if (!exists) {
					createSymbol(system);
				}
			});
			setAreSystemsCreated(true);
			onSystemsCreated();
		}
	}, [viewer, systemIds, currentArea, createSymbol]);

	const toggleSystemsAndEntities = (show: boolean) => {
		if (show) {
			viewer.entities.values.forEach((entity) => {
				if (entity.id.includes("symbol")) {
					entity.show = true;
				}
			});
		} else {
			viewer.entities.values.forEach((entity) => {
				if (entity.id.includes("symbol")) {
					entity.show = false;
				}
			});

			Object.keys(allEntities).forEach((key) => {
				if (key.includes("symbol")) {
					resetAgentIdEntities(key, false);
				}
			});
		}
	};

	useEffect(() => {
		if (viewer) {
			viewer.entities.suspendEvents();
			if (visible && !areSystemsCreated) drawSystems();
			else if (visible && areSystemsCreated) {
				toggleSystemsAndEntities(true);
			} else {
				toggleSystemsAndEntities(false);
			}
			viewer.entities.resumeEvents();
		}
	}, [viewer, visible, side, drawSystems, areSystemsCreated]);

	return null;
};

export default SystemsLayer;
